<template>
  <div class="opc-coupon-form opc-coupon-form__regular-coupon">
    <div class="opc-coupon-form__inner" v-show="!isFromCancelCampaign">
      <zm-form label-position="inline" class="opc-coupon-form__coupon-code">
        <zm-form-item :label="$t('billing.coupon.coupon_input_placeholder')" :for="'opc-form-label-coupon-code' + uid">
          <zm-input
            class="opc-coupon-form__coupon-code-input"
            :class="isShowMessage ? 'opc-coupon-form__input-error' : ''"
            :id="'opc-form-label-coupon-code' + uid"
            v-model.trim="couponCode"
            :disabled="disableCouponInputForm"
            ref="couponInput"
          >
          <span slot="suffix">
            <button
              type="button"
              v-show="showClearCta"
              aria-label="Clear coupon code"
              class="zm-input__icon zm-icon-error zm-input__clear"
              @click="clearCouponCode"
            />
            <i v-if="isActive" :class="inputIconClassName"></i>
          </span>
          </zm-input>
        </zm-form-item>
      </zm-form>
      <div v-if="isShowActionButton" :class="isShowActionButton ? 'opc-coupon-form__btn-wrapper' : ''">
        <zm-button
          round
          v-if="showApplyCta"
          :disabled="applyDisabled"
          :loading="isLoading"
          aria-label="Add coupon"
          class="opc-coupon-form__apply-btn text-capitalize"
          @click="applyCoupon"
        >
          {{ $t('billing.common.add') }}
        </zm-button>
        <zm-button
          round
          v-if="showCancelCta"
          :disabled="cancelDisabled"
          aria-label="Remove coupon"
          class="opc-coupon-form__cancel-btn text-capitalize"
          @click="removeCoupon">
          {{ $t('billing.common.remove') }}
        </zm-button>
      </div>
    </div>
    <div :role="isActive ? '' : 'alert'" ref="couponMsgRef" v-show="isShowMessage" :class="{ 'error-msg': !isActive }"
         class="opc-coupon-form__message">
      {{ message }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { debounce, isEqual, trim, uniqueId } from 'lodash';
import { couponFetchStatus, couponSourceTypeMap, couponTypes } from '@/views/config/coupon/helper/constant';
import { COUPON_ACTION_TYPES } from '@/store/modules/coupon';
import { getDefaultCouponState, showCouponTermsMessage } from '@/views/config/coupon/helper/utils';
import { pageConfig } from '@/common/service/page-config';
import { isEmptyVal } from '@/utils';
import { popUpModalByRemoveCoupon } from '@/views/config/partner-refer/helper/validator';

let persistLocalCouponCode = "";

export default {
  name: 'opc-coupon-form',
  data() {
    return {
      couponCode: persistLocalCouponCode,
      uid: uniqueId(),
    };
  },
  computed: {
    ...mapGetters([
      'couponState',
      'couponMessage',
      'verifyCouponStatus',
      'tncMessage',
      'getSelectedPlans',
      'getSelectedPlansWithQuantityMap',
      'getSelectedFreeTrialPlansWithQuantityMap',
      'cartIsReady',
      'isReplaceSubscriptionMarketingCoupon',
      'soldToContact',
      'partnerRefer',
      'showInEligibleMsg',
      'addressInfo',
      'isFromCancelCampaign',
      'forNewSubscriptionFlow'
    ]),
    needCallValidateApiInCheckoutPage() {
      return true;
    },
    message() {
      return this.couponMessage;
    },
    disableCouponInputForm() {
      return this.isActive || this.isReplaceSubscriptionMarketingCoupon || this.isLoading;
    },
    showClearCta() {
      return !this.isActive && this.couponCode && !this.isReplaceSubscriptionMarketingCoupon;
    },
    isActive() {
      return !!(
        this.couponState.couponCode &&
        this.verifyCouponStatus === couponFetchStatus.successfully &&
        this.couponState.couponType !== couponTypes.NO_DISCOUNT
      );
    },
    isLoading() {
      return this.verifyCouponStatus === couponFetchStatus.pending;
    },
    isShowMessage() {
      return !!this.message && !!this.couponCode;
    },
    isReady() {
      return trim(this.couponCode).length > 0;
    },
    fromPreloadLink() {
      return this.couponState.fromPreloadLink;
    },
    isShowActionButton() {
      return this.forNewSubscriptionFlow
        && (this.showCancelCta || this.showApplyCta);
    },
    showApplyCta() {
      return this.isActive === false;
    },
    showCancelCta() {
      return this.isActive && this.couponState.allowChange;
    },
    applyDisabled() {
      return this.isReady === false;
    },
    cancelDisabled() {
      return false;
    },
    couponInputDisabled() {
      return this.isLoading || this.isActive;
    },
    inputIconClassName() {
      if (this.isActive) {
        return 'zm-icon-success';
      }

      return '';
    },
    isNewSubscription() {
      return this.$context.is.buyNow || this.$context.is.basic2pro;
    },
    forVerifyCampaignLink() {
      const { couponSourceType, couponType } = this.couponState || {};

      // shopping cart load done
      // have coupon code
      // must from campaign link
      // coupon type should be marketing
      // threshold or at&t coupon don't need auto call verify api
      return this.cartIsReady
        && !isEmptyVal(this.couponCode)
        && this.fromPreloadLink
        && couponType === couponTypes.MARKETING
        && !([couponSourceTypeMap.threshold].includes(couponSourceType))
    },
    forVerifyCouponCondition() {
      return this.couponCode
        && this.isNewSubscription
        && !this.fromPreloadLink;
    },
  },
  methods: {
    ...mapMutations([
      'resetCouponState',
      'setVerifyCouponStatus',
      'setCouponState',
      'setCouponMessage',
      'setLastShowInEligibleMsg'
    ]),
    ...mapActions({
      verifyCoupon: COUPON_ACTION_TYPES.verifyCoupon,
      preCheckCoupon: COUPON_ACTION_TYPES.preCheckCoupon,
    }),
    autoFocusWithAlertErrorMsg() {
      if (!this.isActive && this.isShowMessage) {
        this.focus();
      }
    },
    clearCouponCode() {
      this.couponCode = '';
      this.focus();
    },
    async applyCoupon() {
      const couponCode = this.couponCode;
      if (!this.couponState.allowChange) {
        return;
      }
      await this.verifyCoupon({ couponCode });

      // config product page don't need pop tnc dialog, will pop up when the customer click the config product page continue button
      !this.$context.is.configPage && (await this.showTCMessage());
      // deal ax
      this.autoFocusWithAlertErrorMsg();
    },
    async showTCMessage() {
      try {
        await showCouponTermsMessage(this.tncMessage);
      } catch (e) {
        this.removeCoupon();
      }
    },
    async removeCoupon() {
      await popUpModalByRemoveCoupon(this.couponCode);
      const couponCode = this.couponCode;
      this.couponCode = '';
      this.resetCouponState();

      // event tracking
      this.$tracking.couponInteraction(`coupon remove - ${couponCode}`, couponCode);
    },
    focus() {
      this.$refs.couponInput?.focus?.();
    },
    preResetCouponState() {
      // stay coupon code here, in order to solve the automatic application of the coupon, the discount value obtained is the old one in the previous interface
      this.setCouponState({ ...getDefaultCouponState(), couponCode: this.couponState.couponCode })
      this.setVerifyCouponStatus(couponFetchStatus.init);
    },
    setPersistLocalCouponCode() { // in order to persist coupon code, when the coupon validate fail then switch router
      persistLocalCouponCode = this.couponCode;
    },
    autoVerifyCouponAction() {
      if (this.partnerRefer?.rules?.validateByCoupon) {
        this.setLastShowInEligibleMsg(this.showInEligibleMsg);
      }

      if (this.forVerifyCouponCondition) { // for apply coupon api
        this.preResetCouponState();
        this.getPlansCouponInfo?.(this.couponCode);
      } else if (this.forVerifyCampaignLink) { // for verify marketing coupon or threshold coupon or
        this.validateCampaignCoupon?.(this.couponCode);
      }
    },
  },
  watch: {
    getSelectedPlansWithQuantityMap: {
      handler(newVal, oldVal) {
        // As long as the shopping cart changes, need call api is required
        // add/remove product or increase/decrease plan's license
        const isNeedCall = isEqual(newVal, oldVal) === false;

        // if the shopping cart can not change, so don't need call any api with coupon
        if (!isNeedCall) return;

        this.autoVerifyCouponAction();
      },
    },
    getSelectedFreeTrialPlansWithQuantityMap(newVal, oldVal) {
      if (isEqual(newVal, oldVal) === false) {
        this.autoVerifyCouponAction();
      }
    },
    verifyCouponStatus: {
      immediate: true,
      handler(val) {
        switch (val) {
          case couponFetchStatus.successfully:
            this.couponCode = this.couponState.couponCode;
            this.setCouponMessage(this.$t('billing.coupon.success_tips'));
            this.$tracking.couponInteraction(`coupon add - ${this.couponCode}`, this.couponCode);
            break;
          case couponFetchStatus.failure: {
            // In order to cache the coupon code on the component instance when verification fails, so that the user can re-verify
            this.couponCode = this.couponCode || (pageConfig.persistCoupon);
            this.$nextTick(() => {
              this.setCouponMessage(this.couponState?.errorMessage);
              this.$tracking.couponInteraction(`coupon error - ${this.couponState?.errorCode} - ${this.couponCode}`, this.couponCode);
            });
            break;
          }
          case couponFetchStatus.pending: {
            this.setCouponMessage('');

            // when the customer refresh current page, will call coupon api in init shopping cart with session storage coupon state
            if (this.couponState?.couponCode) {
              this.couponCode = this.couponState.couponCode;
            }

            break;
          }
          default:
            this.setCouponMessage('');
            break;
        }

        this.setPersistLocalCouponCode();
      },
    },
    couponCode: {
      handler() {
        this.setCouponMessage('');
        this.setPersistLocalCouponCode();
      },
    },
    "soldToContact.country": {
      handler() {
        this.autoVerifyCouponAction();
      },
    },
    "addressInfo.usageType": {
      handler() {
        this.autoVerifyCouponAction();
      },
    }
  },
  created() {
    this.getPlansCouponInfo = debounce((couponCode) => {
      this.verifyCoupon({ couponCode });
    }, 500);

    this.validateCampaignCoupon = debounce((couponCode) => {
      this.preCheckCoupon({ couponCode });
    }, 500);

    // just init coupon form need auto call apply or validate api
    if (this.needCallValidateApiInCheckoutPage) {
      if (this.$context.is.checkoutPage
        && this.forVerifyCouponCondition) {
        this.verifyCoupon({ couponCode: this.couponCode });
      } else if (this.forVerifyCampaignLink) {
        // just for first call validate api (post check)
        this.preCheckCoupon({ couponCode: this.couponCode });
      }
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-coupon-form {
  &__inner {
    display: flex;
    align-items: center;
  }

  &__input-error {
    .zm-input__inner {
      border-color: #DE2828 !important;
    }
  }

  &__coupon-code-input {
    .zm-input__label {
      top: 16px;
    }
  }

  &__coupon-code {
    flex: 1;

    #opc-form-label-coupon-code1 {
      @include media(desktop){
        scroll-margin-bottom: 200px;
      }
    }

    .zm-form-item {
      margin-bottom: 0px;
    }

    .zm-input__suffix {
      margin-right: 9px;
    }

    .zm-icon-success, .zm-icon-error {
      font-size: 16px;
    }

    .zm-icon-success {
      color: #4A7D5C;
    }

    .zm-input.is-disabled .zm-input__inner {
      background: white !important;
      border-color: #444b53 !important;
      color: #131619 !important;

      @include media(mobile, tablet) {
        opacity: 1;
      }
    }
  }

  &__btn-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 115px;
  }

  &__apply-btn, &__cancel-btn {
    height: 34px;
    color: #00053D;
    padding: 6px 20px !important;
    font-size: 16px;
    font-weight: 600;
  }

  &__message {
    margin-top: 8px;
    color: #00053D;
    font-size: 12px;
    font-weight: 500;

    &.error-msg {
      color: #DE2828;
    }
  }
}
</style>
